import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { fmtDate } from '../utils/format';
import {
  listMeetings,
  getMeeting,
  createMeeting,
  updateMeeting,
  confirmMeeting,
  removeMeeting,
  meetingUsers,
  listMeetingGroups,
  createMeetingGroup,
} from '../api/meetings';

const MEETING_NAMES = ['Họp BĐH', 'Họp phòng kinh doanh', 'Họp bất thường'];
const weekOfMonth = (d) => Math.min(5, Math.ceil(d.getDate() / 7));

// Thoát ký tự HTML để nhúng an toàn vào file Word.
const esc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Xuất biên bản ra file Word (.doc — HTML tương thích Word). rows = danh sách báo cáo hiện tại.
function downloadMeetingDoc(m, rows) {
  const title = `Biên bản ${m.name} - ${m.groupLabel}`;
  const valid = (rows || []).filter((r) => (r.reporter || '').trim() || (r.content || '').trim());
  const reportRows = valid.length
    ? valid
        .map(
          (r) =>
            `<tr><td style="border:1px solid #888;padding:6px;vertical-align:top;width:28%">${esc(
              r.reporter
            )}</td><td style="border:1px solid #888;padding:6px;vertical-align:top">${esc(r.content).replace(
              /\n/g,
              '<br>'
            )}</td></tr>`
        )
        .join('')
    : '<tr><td style="border:1px solid #888;padding:6px" colspan="2">(Chưa có nội dung)</td></tr>';

  const fmtD = (d) => (d ? fmtDate(d) : '—');
  const exportedAt = new Date();
  const html = `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${esc(
    title
  )}</title></head><body style="font-family:'Times New Roman',serif;font-size:13pt;color:#000">
    <h2 style="text-align:center;text-transform:uppercase">${esc(title)}</h2>
    <p><b>Tên cuộc họp:</b> ${esc(m.name)}</p>
    <p><b>Kỳ họp (nhóm):</b> ${esc(m.groupLabel)}</p>
    <p><b>Ngày họp:</b> ${fmtD(m.date)}</p>
    <p><b>Người tham dự:</b> ${esc((m.participants || []).join(', ') || '—')}</p>
    <p><b>Người ghi biên bản:</b> ${esc(m.createdByName || '—')}</p>
    <p><b>Trạng thái:</b> ${m.status === 'confirmed' ? `Đã hoàn thành (lúc ${fmtD(m.confirmedAt)})` : 'Nháp'}</p>
    <p><b>Ngày tạo biên bản:</b> ${fmtD(m.createdAt)} &nbsp;·&nbsp; <b>Xuất file lúc:</b> ${fmtDate(exportedAt)} ${String(
    exportedAt.getHours()
  ).padStart(2, '0')}:${String(exportedAt.getMinutes()).padStart(2, '0')}</p>
    <h3>Nội dung cuộc họp</h3>
    <table style="border-collapse:collapse;width:100%">
      <thead><tr>
        <th style="border:1px solid #888;padding:6px;background:#eee;text-align:left">Người báo cáo</th>
        <th style="border:1px solid #888;padding:6px;background:#eee;text-align:left">Nội dung báo cáo</th>
      </tr></thead>
      <tbody>${reportRows}</tbody>
    </table>
  </body></html>`;

  const fileName = `${title}`.replace(/[\\/:*?"<>|]/g, '-') + '.doc';
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hms = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;

// ---- Form tạo nhóm người tham gia (hiển thị trong cùng modal) ----
function GroupForm({ onDone, onCancel }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState([]); // userId[]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    meetingUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const toggle = (id) => setPicked((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  async function save() {
    if (saving) return;
    if (!name.trim()) return toast('Nhập tên nhóm', 'warn');
    setSaving(true);
    try {
      const members = users.filter((u) => picked.includes(u.id)).map((u) => ({ user: u.id, name: u.name }));
      await createMeetingGroup({ name: name.trim(), members });
      toast('Đã tạo nhóm người tham gia');
      onDone();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3>👥 Tạo nhóm người tham gia</h3>
      <div className="field">
        <label>Tên nhóm</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: BĐH, PKD GTS…" />
      </div>
      <div className="field">
        <label>Chọn thành viên cố định ({picked.length})</label>
        <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
          {users.length ? (
            users.map((u) => (
              <label key={u.id} className="checkrow" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} />
                <div className="meta">
                  <div className="nm">{u.name}</div>
                  {u.code && <div className="dt">{u.code}</div>}
                </div>
              </label>
            ))
          ) : (
            <div className="empty">Đang tải danh sách nhân viên…</div>
          )}
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn ghost" onClick={onCancel}>
          Quay lại
        </button>
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Lưu nhóm'}
        </button>
      </div>
    </div>
  );
}

// ---- Modal tạo lịch họp ----
// Ô nội dung TỰ NỚI CAO theo số dòng chữ — không để mỗi ô một thanh cuộn riêng (đọc biên bản
// phải kéo từng ô rất khó chịu). Cao tối thiểu `rows` dòng, dài bao nhiêu nới bấy nhiêu.
function AutoTextarea({ value, rows = 3, style, ...rest }) {
  const ref = useRef(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto'; // co về 0 trước, nếu không scrollHeight giữ nguyên chiều cao cũ khi xoá bớt chữ
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  // useLayoutEffect (không phải useEffect): nới TRƯỚC khi trình duyệt vẽ -> mở biên bản có sẵn nội dung
  // không bị nháy từ 3 dòng bung ra.
  useLayoutEffect(fit, [value, fit]);
  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value}
      onInput={fit}
      style={{ ...style, overflowY: 'hidden', resize: 'none' }}
      {...rest}
    />
  );
}

function CreateModal({ onClose, onCreated }) {
  const toast = useToast();
  const now = new Date();
  const [name, setName] = useState(MEETING_NAMES[0]);
  const [date, setDate] = useState(ymd(now));
  const [week, setWeek] = useState(weekOfMonth(now));
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [parts, setParts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState('meeting'); // 'meeting' | 'group'
  const [saving, setSaving] = useState(false);

  const loadGroups = () => listMeetingGroups().then(setGroups).catch(() => setGroups([]));
  useEffect(() => {
    loadGroups();
  }, []);

  const togglePart = (p) => setParts((arr) => (arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]));

  // Khi đổi ngày họp -> tự gợi ý tuần/tháng/năm (vẫn cho sửa tay).
  const onDate = (v) => {
    setDate(v);
    const d = new Date(v);
    if (!isNaN(d)) {
      setWeek(weekOfMonth(d));
      setMonth(d.getMonth() + 1);
      setYear(d.getFullYear());
    }
  };

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const m = await createMeeting({ name, date, week, month, year, participants: parts });
      toast('Đã tạo lịch họp — bắt đầu ghi biên bản');
      onClose(); // đóng modal tạo trước...
      onCreated(m._id); // ...rồi mở trình ghi biên bản (mở sau nên không bị triệt tiêu)
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSaving(false);
    }
  }

  // Đang ở chế độ tạo nhóm -> hiển thị form nhóm trong cùng modal.
  if (mode === 'group') {
    return (
      <GroupForm
        onCancel={() => setMode('meeting')}
        onDone={() => {
          loadGroups();
          setMode('meeting');
        }}
      />
    );
  }

  return (
    <div>
      <h3>🗓️ Tạo lịch họp</h3>

      <div className="field">
        <label>Tên cuộc họp</label>
        <select value={name} onChange={(e) => setName(e.target.value)}>
          {MEETING_NAMES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <div className="row between" style={{ alignItems: 'center' }}>
          <label style={{ margin: 0 }}>Người tham gia</label>
          <button type="button" className="btn ghost sm" onClick={() => setMode('group')}>
            + Tạo nhóm
          </button>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {groups.length ? (
            groups.map((g) => (
              <button
                type="button"
                key={g._id}
                className={`filterBtn${parts.includes(g.name) ? ' on' : ''}`}
                title={g.members?.map((m) => m.name).join(', ') || 'Chưa có thành viên'}
                onClick={() => togglePart(g.name)}
              >
                {g.name}
              </button>
            ))
          ) : (
            <span className="small muted">Chưa có nhóm — bấm “+ Tạo nhóm” để tạo.</span>
          )}
        </div>
      </div>

      <div className="field">
        <label>Ngày họp</label>
        <input type="date" value={date} onChange={(e) => onDate(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>

      <div className="field">
        <label>Nhóm (kỳ họp)</label>
        <div className="row" style={{ gap: 8 }}>
          <select style={{ width: 'auto' }} value={week} onChange={(e) => setWeek(Number(e.target.value))}>
            {[1, 2, 3, 4, 5].map((w) => (
              <option key={w} value={w}>
                Tuần {w}
              </option>
            ))}
          </select>
          <select style={{ width: 'auto' }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((mo) => (
              <option key={mo} value={mo}>
                Tháng {mo}
              </option>
            ))}
          </select>
          <select style={{ width: 'auto' }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 1, year, year + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="small muted" style={{ marginTop: 6 }}>
          Mọi cuộc họp cùng tuần/tháng/năm sẽ gom chung 1 nhóm: <b>Tuần {week} tháng {month}/{year}</b>.
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <button className="btn ghost" onClick={onClose}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? 'Đang tạo…' : 'Tạo & ghi biên bản'}
        </button>
      </div>
    </div>
  );
}

// ---- Modal ghi biên bản (tự lưu) — dạng bảng: người báo cáo | nội dung ----
function EditorModal({ id, onClose, onChanged }) {
  const toast = useToast();
  const [m, setM] = useState(null);
  const [rows, setRows] = useState([]); // [{reporter, content}]
  const [reporters, setReporters] = useState([]); // tên thành viên các nhóm tham gia
  const [save, setSave] = useState('');
  const timer = useRef(null);
  const latest = useRef([]);
  const dirty = useRef(false);

  useEffect(() => {
    Promise.all([getMeeting(id), listMeetingGroups().catch(() => [])]).then(([d, groups]) => {
      setM(d);
      // Mặc định tối thiểu 6 dòng cho tiện viết (chỉ đệm dòng trống khi còn nháp).
      const saved = d.reports && d.reports.length ? d.reports : [];
      const init = [...saved];
      if (d.status !== 'confirmed') while (init.length < 6) init.push({ reporter: '', content: '' });
      if (!init.length) init.push({ reporter: '', content: '' });
      setRows(init);
      latest.current = init;
      // Người báo cáo = thành viên của các nhóm tham gia cuộc họp này.
      const partSet = new Set(d.participants || []);
      const names = new Set();
      groups.forEach((g) => {
        if (partSet.has(g.name)) (g.members || []).forEach((mem) => mem.name && names.add(mem.name));
      });
      setReporters([...names]);
    });
  }, [id]);

  async function persist() {
    if (!dirty.current) return;
    setSave('saving');
    try {
      await updateMeeting(id, { reports: latest.current });
      dirty.current = false;
      setSave('Đã lưu ' + hms(new Date()));
    } catch (err) {
      setSave('⚠️ Lưu lỗi — thử lại');
    }
  }

  // Lưu nốt khi đóng (kể cả chưa kịp debounce).
  useEffect(() => () => { if (dirty.current) updateMeeting(id, { reports: latest.current }).catch(() => {}); }, [id]);

  const scheduleSave = (next) => {
    setRows(next);
    latest.current = next;
    dirty.current = true;
    setSave('Đang gõ…');
    clearTimeout(timer.current);
    timer.current = setTimeout(persist, 600);
  };

  const updateRow = (i, field, val) => scheduleSave(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const addRow = () => scheduleSave([...rows, { reporter: '', content: '' }]);
  const removeRow = (i) => scheduleSave(rows.filter((_, idx) => idx !== i));

  async function doConfirm() {
    await persist();
    if (!await confirm('Hoàn thành biên bản? Sau khi hoàn thành sẽ không sửa được nội dung.')) return;
    try {
      await confirmMeeting(id);
      toast('Đã hoàn thành biên bản');
      onChanged();
      onClose();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function close() {
    clearTimeout(timer.current);
    await persist();
    onChanged();
    onClose();
  }

  if (!m) return <div className="empty">Đang tải...</div>;
  const locked = m.status === 'confirmed';

  return (
    <div>
      <h3>📝 Biên bản: {m.name}</h3>
      <div className="small muted" style={{ marginBottom: 6 }}>
        {m.groupLabel} · Ngày họp {fmtDate(m.date)} · Người ghi: {m.createdByName || '—'}
        {locked && <span className="chip done" style={{ marginLeft: 8 }}>Đã hoàn thành</span>}
      </div>
      {m.participants && m.participants.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <span className="small muted">Người tham gia: </span>
          {m.participants.map((p) => (
            <span className="tag" key={p}>
              {p}
            </span>
          ))}
        </div>
      )}

      <datalist id={`reporters-${id}`}>
        {reporters.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>

      {/* Tiêu đề 2 cột */}
      <div className="row" style={{ gap: 10, marginBottom: 6 }}>
        <div style={{ width: 220 }} className="small muted">NGƯỜI BÁO CÁO</div>
        <div style={{ flex: 1 }} className="small muted">NỘI DUNG BÁO CÁO</div>
        {!locked && <div style={{ width: 30 }} />}
      </div>

      <div style={{ maxHeight: '52vh', overflow: 'auto' }}>
        {rows.map((r, i) => (
          <div key={i} className="row" style={{ gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
            <input
              list={`reporters-${id}`}
              style={{ width: 220 }}
              value={r.reporter}
              readOnly={locked}
              placeholder="Chọn / nhập tên"
              onChange={(e) => updateRow(i, 'reporter', e.target.value)}
            />
            <AutoTextarea
              rows={3}
              style={{ flex: 1, fontFamily: 'inherit', lineHeight: 1.5 }}
              value={r.content}
              readOnly={locked}
              placeholder="Nội dung báo cáo…"
              onChange={(e) => updateRow(i, 'content', e.target.value)}
            />
            {!locked && (
              <button
                className="btn ghost sm danger"
                style={{ width: 30, padding: '6px 0' }}
                title="Xóa dòng"
                onClick={() => removeRow(i)}
                disabled={rows.length <= 1}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {!locked && (
        <button className="btn ghost sm" style={{ marginTop: 4 }} onClick={addRow}>
          + Thêm dòng báo cáo
        </button>
      )}

      <div className="row between" style={{ marginTop: 14, flexWrap: 'wrap', gap: 8 }}>
        <span className="small muted">{locked ? `Hoàn thành lúc ${fmtDate(m.confirmedAt)}` : save || 'Tự lưu khi gõ'}</span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn ghost" onClick={() => downloadMeetingDoc(m, rows)} title="Tải biên bản về file Word">
            ⬇ Tải Word
          </button>
          <button className="btn ghost" onClick={close}>
            Đóng
          </button>
          {m.canWrite && !locked && (
            <button className="btn ok" onClick={doConfirm}>
              Hoàn thành biên bản
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Timeline tuần: mũi tên 2 đầu kéo qua các tuần (tháng trước/sau) ----
function WeekTimeline({ items, onOpen }) {
  const scroller = useRef(null);
  const monthIdx = (d) => d.getFullYear() * 12 + d.getMonth();

  const byKey = {};
  items.forEach((m) => {
    (byKey[m.groupKey] = byKey[m.groupKey] || []).push(m);
  });

  const dates = items.map((m) => new Date(m.date));
  dates.push(new Date());
  // Phủ trọn các tháng trong năm: từ tháng 1 của năm sớm nhất đến tháng 12 của năm muộn nhất,
  // cộng thêm trọn 1 năm sau để xem nối tiếp sang các tháng năm mới.
  const minYear = Math.floor(Math.min(...dates.map(monthIdx)) / 12);
  const maxYear = Math.floor(Math.max(...dates.map(monthIdx)) / 12) + 1;
  const minI = minYear * 12; // tháng 1 (index 0) của năm sớm nhất
  const maxI = maxYear * 12 + 11; // tháng 12 của năm muộn nhất +1

  const cells = [];
  for (let i = minI; i <= maxI; i++) {
    const y = Math.floor(i / 12);
    const mo = i % 12; // 0-based
    const weeks = Math.ceil(new Date(y, mo + 1, 0).getDate() / 7);
    for (let w = 1; w <= weeks; w++) {
      const key = `${y}-${String(mo + 1).padStart(2, '0')}-W${w}`;
      cells.push({ key, w, month: mo + 1, year: y, meetings: byKey[key] || [] });
    }
  }

  const now = new Date();
  const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-W${weekOfMonth(now)}`;

  // Cuộn tới tuần hiện tại khi mở.
  useEffect(() => {
    const el = scroller.current?.querySelector(`[data-key="${nowKey}"]`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [nowKey]);

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 460, behavior: 'smooth' });

  return (
    <div className="card">
      <div className="tl-wrap">
        <button className="tl-arrow" onClick={() => scroll(-1)} title="Tuần trước">
          ‹
        </button>
        <div className="tl-scroll" ref={scroller}>
          {cells.map((c) => (
            <div
              key={c.key}
              data-key={c.key}
              className={`tl-cell${c.meetings.length ? ' has' : ''}${c.key === nowKey ? ' now' : ''}`}
            >
              <div className="tl-head">
                Tuần {c.w} · th.{c.month}/{c.year}
              </div>
              {c.meetings.length ? (
                c.meetings.map((m) => (
                  <div
                    key={m._id}
                    className="tl-item"
                    title={`${m.name} — ${m.status === 'confirmed' ? 'Đã hoàn thành' : 'Nháp'}`}
                    onClick={() => onOpen(m._id)}
                  >
                    {m.status === 'confirmed' ? (
                      <span style={{ color: 'var(--ok)', fontWeight: 800 }}>✓ </span>
                    ) : (
                      '• '
                    )}
                    {m.name}
                  </div>
                ))
              ) : (
                <div className="tl-empty">—</div>
              )}
            </div>
          ))}
        </div>
        <button className="tl-arrow" onClick={() => scroll(1)} title="Tuần sau">
          ›
        </button>
      </div>
    </div>
  );
}

export default function MeetingsPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [items, setItems] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    listMeetings()
      .then((d) => {
        setItems(d.items);
        setCanWrite(d.canWrite);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEditor = (id) => openModal(<EditorModal id={id} onClose={closeModal} onChanged={load} />, { wide: true });
  // Sau khi tạo: nạp lại danh sách (để hiện bản nháp) rồi mở trình ghi biên bản.
  const afterCreate = (id) => {
    load();
    openEditor(id);
  };

  async function del(mtg) {
    if (!await confirm(`Xóa biên bản "${mtg.name}"?`)) return;
    try {
      await removeMeeting(mtg._id);
      toast('Đã xóa biên bản');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  // Lọc theo từ khóa: tên họp, nhóm kỳ họp, người ghi, người tham gia, người báo cáo & nội dung báo cáo.
  const q = search.trim().toLowerCase();
  const matchMeeting = (m) => {
    if (!q) return true;
    const hay = [
      m.name,
      m.groupLabel,
      m.createdByName,
      (m.participants || []).join(' '),
      m.content,
      (m.reports || []).map((r) => `${r.reporter} ${r.content}`).join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  };
  // Mặc định danh sách chỉ hiện bản NHÁP; khi tìm kiếm thì hiện kết quả (mọi trạng thái).
  const filtered = q ? items.filter(matchMeeting) : items.filter((m) => m.status !== 'confirmed');

  // Gom theo nhóm tuần/tháng (giữ thứ tự mới nhất trước).
  const groups = [];
  const idx = {};
  filtered.forEach((m) => {
    if (idx[m.groupLabel] == null) {
      idx[m.groupLabel] = groups.length;
      groups.push({ label: m.groupLabel, rows: [] });
    }
    groups[idx[m.groupLabel]].rows.push(m);
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Lịch họp</h2>
          <div className="sub">Biên bản cuộc họp — phòng CSKH ghi nội dung, tự lưu chống mất dữ liệu.</div>
        </div>
        {canWrite && (
          <button className="btn" onClick={() => openModal(<CreateModal onClose={closeModal} onCreated={afterCreate} />)}>
            + Tạo lịch họp
          </button>
        )}
      </div>

      {!loading && items.length > 0 && <WeekTimeline items={items} onOpen={openEditor} />}

      {!loading && items.length > 0 && (
        <div className="row" style={{ marginBottom: 16 }}>
          <input
            style={{ maxWidth: 420 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Tìm theo tên họp, người ghi, người báo cáo, nội dung…"
          />
          {search && (
            <button className="btn ghost sm" onClick={() => setSearch('')}>
              Xóa lọc
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="card">
          <div className="empty">Đang tải...</div>
        </div>
      ) : !groups.length ? (
        <div className="card">
          <div className="empty">{q ? 'Không tìm thấy cuộc họp phù hợp' : 'Không có bản nháp nào'}</div>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.label} style={{ marginBottom: 8 }}>
            <div className="small muted" style={{ fontWeight: 700, margin: '2px 2px 8px' }}>📅 {g.label} ({g.rows.length})</div>
            {g.rows.map((m) => (
              <div key={m._id} className="card" style={{ marginBottom: 10 }}>
                <div className="row between" style={{ alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{m.name}</span>
                      <span className={`chip ${m.status === 'confirmed' ? 'done' : 'review'}`}>
                        {m.status === 'confirmed' ? 'Đã hoàn thành' : 'Nháp'}
                      </span>
                    </div>
                    <div className="small muted mono" style={{ marginTop: 4 }}>
                      {fmtDate(m.date)}{m.groupLabel ? ` · ${m.groupLabel}` : ''} · Người ghi {m.createdByName || '—'}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6, flex: '0 0 auto' }}>
                    <button className="btn ghost sm" onClick={() => openEditor(m._id)}>
                      {canWrite && m.status !== 'confirmed' ? 'Ghi biên bản' : 'Xem'}
                    </button>
                    {canWrite && (
                      <button className="btn ghost sm danger" onClick={() => del(m)}>
                        Xóa
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </>
  );
}
